import React, { useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { firestore } from "../config/config";

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

const ROLES = {
  ADMIN: "admin",
  DESIGN_HEAD: "designerHead",
  USER: "designer",
};

// Modal component for event creation and editing
function EventModal({ isOpen, onClose, event, allUsers, userId, role, onSave, onDelete }) {
  const [title, setTitle] = useState(event?.title || "");
  const [startDate, setStartDate] = useState(event?.start || new Date());
  const [endDate, setEndDate] = useState(event?.end || new Date());
  const [assignedUsers, setAssignedUsers] = useState(event?.assignedTo || [userId]);

  useEffect(() => {
    if (event) {
      // Editing an existing event
      setTitle(event.title || "");
      setStartDate(event.start || new Date());
      setEndDate(event.end || new Date());
      setAssignedUsers(event.assignedTo || [userId]);
    } else {
      // Creating a new event
      const now = new Date();
      const oneHourLater = new Date(now);
      oneHourLater.setHours(now.getHours() + 1);
      
      setTitle("");
      setStartDate(now);
      setEndDate(oneHourLater);
      setAssignedUsers([userId]);
    }
  }, [event, userId]);

  const handleSave = () => {
    if (!title.trim()) {
      alert("Please enter a title for the event");
      return;
    }

    onSave({
      ...event,
      title,
      description: "",
      start: startDate,
      end: endDate,
      assignedTo: assignedUsers,
    });
    onClose();
  };

  const handleUserCheckbox = (uid) => {
    if (assignedUsers.includes(uid)) {
      setAssignedUsers(assignedUsers.filter(id => id !== uid));
    } else {
      setAssignedUsers([...assignedUsers, uid]);
    }
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      onDelete(event);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "8px",
        width: "90%",
        maxWidth: "500px",
        maxHeight: "90vh",
        overflow: "auto"
      }}>
        <h2 style={{color:'#000'}}>{event ? "Edit Event" : "Create New Event"}</h2>
        
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px" }}>Title:</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
            placeholder="Enter event title"
          />
        </div>
        
        <div style={{ marginBottom: "15px", display: "flex", gap: "10px" }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Start Date:</label>
            <input
              type="datetime-local"
              value={format(startDate, "yyyy-MM-dd'T'HH:mm")}
              onChange={(e) => setStartDate(new Date(e.target.value))}
              style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>
          
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", marginBottom: "5px" }}>End Date:</label>
            <input
              type="datetime-local"
              value={format(endDate, "yyyy-MM-dd'T'HH:mm")}
              onChange={(e) => setEndDate(new Date(e.target.value))}
              style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
            />
          </div>
        </div>
        
        {(role === ROLES.ADMIN || role === ROLES.DESIGN_HEAD) && (
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Assign to:</label>
            <div style={{ maxHeight: "150px", overflowY: "auto", border: "1px solid #ccc", borderRadius: "4px", padding: "8px" }}>
              {allUsers.map((user) => (
                <div key={user.uid} style={{ marginBottom: "5px" }}>
                  <label style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      checked={assignedUsers.includes(user.uid)}
                      onChange={() => handleUserCheckbox(user.uid)}
                      style={{ marginRight: "8px" }}
                    />
                    {user.name || user.email || user.displayName || "Unknown User"}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
          {event && (
            <button
              onClick={handleDelete}
              style={{ padding: "8px 16px", backgroundColor: "#ff4d4f", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
            >
              Delete
            </button>
          )}
          <button
            onClick={onClose}
            style={{ padding: "8px 16px", backgroundColor: "#f0f0f0", border: "none", borderRadius: "4px", cursor: "pointer" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{ padding: "8px 16px", backgroundColor: "#1890ff", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
          >
            {event ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CalendarComponent({ role = ROLES.USER }) {
  const [events, setEvents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Fetch all users from Firestore
  const fetchUsers = async () => {
    try {
      const snapshot = await getDocs(collection(firestore, "users"));
      const userList = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setAllUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users. Please refresh the page.");
    }
  };

  // Fetch events based on user role
  const fetchEvents = async (currentUserId) => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(firestore, "events"));
      const eventList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(event => {
          if (role === ROLES.USER) {
            return event.createdBy === currentUserId ||
              (event.assignedTo && event.assignedTo.includes(currentUserId));
          }
          return true; // Admins and design heads see all events
        })
        .map(event => ({
          ...event,
          start: new Date(event.start),
          end: new Date(event.end),
        }));
      setEvents(eventList);
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Failed to load events. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      setError("User not authenticated");
      setLoading(false);
      return;
    }
    
    setUserId(currentUser.uid);
    
    // Load users and events
    fetchUsers();
    fetchEvents(currentUser.uid);
  }, [role]);

  // Create a new event
  const handleSelectSlot = ({ start, end }) => {
    // Always use current time as the start time and add 1 hour for end time
    const now = new Date();
    const oneHourLater = new Date(now);
    oneHourLater.setHours(now.getHours() + 1);
    
    setSelectedEvent(null);
    setSelectedSlot({ start: now, end: oneHourLater });
    setIsModalOpen(true);
  };

  // Edit an existing event
  const handleSelectEvent = (event) => {
    // Check permissions
    if (role === ROLES.ADMIN || role === ROLES.DESIGN_HEAD || event.createdBy === userId) {
      setSelectedEvent(event);
      setSelectedSlot(null);
      setIsModalOpen(true);
    } else {
      alert("You don't have permission to edit this event.");
    }
  };

  // Handle save event (create or update)
  const handleSaveEvent = async (eventData) => {
    try {
      if (selectedEvent) {
        // Update existing event
        const eventRef = doc(firestore, "events", selectedEvent.id);
        await updateDoc(eventRef, {
          title: eventData.title,
          description: "",
          assignedTo: eventData.assignedTo,
          updatedAt: new Date().toISOString()
        });
        
        // Update local state
        setEvents(events.map(e => 
          e.id === selectedEvent.id 
            ? { ...e, ...eventData } 
            : e
        ));
      } else {
        // Create new event
        const newEvent = {
          title: eventData.title,
          description: "",
          start: eventData.start.toISOString(),
          end: eventData.end.toISOString(),
          createdBy: userId,
          assignedTo: eventData.assignedTo,
          createdAt: new Date().toISOString(),
        };

        const docRef = await addDoc(collection(firestore, "events"), newEvent);
        
        setEvents([...events, { 
          ...newEvent, 
          start: eventData.start, 
          end: eventData.end, 
          id: docRef.id 
        }]);
      }
    } catch (err) {
      console.error("Error saving event:", err);
      alert("Failed to save event. Please try again.");
    }
  };

  // Handle delete event
  const handleDeleteEvent = async (event) => {
    try {
      await deleteDoc(doc(firestore, "events", event.id));
      setEvents(events.filter(e => e.id !== event.id));
    } catch (err) {
      console.error("Error deleting event:", err);
      alert("Failed to delete event. Please try again.");
    }
  };

  // Custom event component to show assignees
  const EventComponent = ({ event }) => {
    const assignedUsers = allUsers.filter(user => 
      event.assignedTo && event.assignedTo.includes(user.uid)
    );
    
    const assigneeNames = assignedUsers
      .map(user => user.name || user.email || user.displayName || "Unknown User")
      .join(", ");
      
    return (
      <div title={event.description || ""}>
        <strong>{event.title}</strong>
        {assigneeNames && (
          <div style={{ fontSize: "0.8em", fontStyle: "italic" }}>
            Assigned to: {assigneeNames}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div>Loading calendar...</div>;
  }

  if (error) {
    return <div style={{ color: "red" }}>{error}</div>;
  }

  return (
    <div style={{ height: 600 }}>
      <div style={{ marginBottom: 16 }}>
        <h2>Event Calendar</h2>
        {role !== ROLES.USER && (
          <div style={{ marginBottom: 8 }}>
            <strong>Role: </strong> 
            {role === ROLES.ADMIN ? "Administrator" : "Design Head"}
            <p style={{ fontSize: "0.9em", fontStyle: "italic" }}>
              You can create events and assign them to any user
            </p>
          </div>
        )}
      </div>
      
      <Calendar
        selectable
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        defaultView="week"
        components={{
          event: EventComponent
        }}
        style={{ height: "calc(100% - 60px)", color: "#000" }}
      />
      
      <EventModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        event={selectedEvent}
        allUsers={allUsers}
        userId={userId}
        role={role}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
      />
    </div>
  );
}

export default CalendarComponent;